import { ROUTE_NAME, STORE } from '~/constants/strings'
import type { PersonnelActivityAnalysis } from '~/constants/types/projectDataVisualizations'
import {
  EnumerationsAnalysisType,
  EnumerationsProportionalAnalyses,
  type QueriesGetOverviewRequest,
  type QueriesGetOverviewResponse,
} from 'client/sratool'
import { api } from '~/services'
import { HNRV2 } from '~/helpers'
import { AxiosError } from 'axios'
import { useChartRefresh } from '~/composables/chart/useChartRefresh'
import type { RouteNamedMap } from 'vue-router/auto-routes'
import { useProjectDataVisualizationsStore } from './projectDataVisualizations'
import {
  GET_OVERVIEW_DATA_FAILED,
  NO_PROJECT_SELECTED,
} from '~/constants/projectDataVisualizations'
import {
  useProjectDataVisualizationSelectionStore,
  type SelectionSnapshot,
} from './projectDataVisualizationsSelection'

export const useProjectOverviewDataVisualizationsStore = defineStore(
  STORE.PROJECT_OVERVIEW_DATA_VISUALIZATIONS,
  () => {
    const ProjectDataVisualizationsStore = useProjectDataVisualizationsStore()
    const { isCurrent } = useProjectDataVisualizationSelectionStore()
    const wfaAPI = api.workforceAnalyticsDataVisualizations
    const data = ref<QueriesGetOverviewResponse>()

    const getData = async (
      selection: SelectionSnapshot,
      requestValidation?: typeof isCurrent
    ) => {
      if (!selection.projectID) {
        ProjectDataVisualizationsStore.displayDataVizErrorPageTakeover(
          NO_PROJECT_SELECTED
        )
        return
      }

      ProjectDataVisualizationsStore.loading.chart = true

      const payload: QueriesGetOverviewRequest = {
        projectID: selection.projectID,
        strataName: selection.strataID
          ? ProjectDataVisualizationsStore.selectedStrata?.strataDisplayName
          : null,
        strataValues: selection.strataValue
          ? [selection.strataValue]
          : undefined,
        groupName: undefined,
      }

      const response = await HNRV2({
        fn: () => wfaAPI.apiSraDataVisualizationsOverviewPost(payload),
      })

      if (requestValidation && !requestValidation(selection)) {
        return
      }

      ProjectDataVisualizationsStore.loading.chart = false
      if (response instanceof AxiosError) {
        ProjectDataVisualizationsStore.displayDataVizErrorPageTakeover(
          GET_OVERVIEW_DATA_FAILED
        )
        data.value = undefined
        return
      }

      data.value = response
      return response
    }

    const getProjectOverviewData = async (selection: SelectionSnapshot) => {
      ProjectDataVisualizationsStore.loading.chart = true

      resetData()

      await Promise.all([getData(selection, isCurrent)])

      ProjectDataVisualizationsStore.loading.chart = false
    }

    const analysisTypeUsageMap = computed<Map<number, number>>(() => {
      const usageMap = new Map()
      data.value?.overviewPAData?.forEach((analysis) => {
        const analysisType = analysis.analysisTypeID
        const proportionalAnalysisType = analysis.proportionalAnalysisID

        if (
          analysisType ===
          EnumerationsAnalysisType.ApplicantsWorkforcePromotionsTerminations
        ) {
          const currentValue: number =
            usageMap.get(proportionalAnalysisType) ?? 0
          usageMap.set(proportionalAnalysisType, currentValue + 1)
        } else {
          const currentValue: number = usageMap.get(analysisType) ?? 0
          usageMap.set(analysisType, currentValue + 1)
        }
      })

      return usageMap
    })

    const analyses = computed(() => {
      const getShowAnalysisName = ({
        analysisType,
        proportionalAnalysisType,
      }: {
        analysisType: EnumerationsAnalysisType
        proportionalAnalysisType: EnumerationsProportionalAnalyses
      }): boolean => {
        if (
          analysisType ===
          EnumerationsAnalysisType.ApplicantsWorkforcePromotionsTerminations
        ) {
          const currentValue =
            analysisTypeUsageMap.value.get(proportionalAnalysisType) ?? 0
          if (currentValue > 1) {
            return true
          }
        } else {
          const currentValue = analysisTypeUsageMap.value.get(analysisType) ?? 0
          if (currentValue > 1) {
            return true
          }
        }

        return false
      }

      // get all used types
      // populate a map with it
      // when going through each analysis, check if map has 2
      // if so, use name

      return (
        data.value?.overviewPAData?.reduce<{
          sraAnalyses: PersonnelActivityAnalysis[]
          proportionalAnalyses: PersonnelActivityAnalysis[]
        }>(
          (analyses, datum) => {
            const showAnalysisName = getShowAnalysisName({
              analysisType: datum.analysisTypeID,
              proportionalAnalysisType: datum.proportionalAnalysisID,
            })
            if (
              datum.analysisTypeID ===
              EnumerationsAnalysisType.ApplicantsWorkforcePromotionsTerminations
            ) {
              const formattedDatum = {
                ...datum,
                uniqueID: `${datum.analysisID}-${datum.proportionalAnalysisID}`,
                showAnalysisName,
              }
              analyses.proportionalAnalyses.push(formattedDatum)
            } else {
              const formattedDatum = {
                ...datum,
                uniqueID: datum.analysisID,
                showAnalysisName,
              }
              analyses.sraAnalyses.push(formattedDatum)
            }

            return analyses
          },
          { sraAnalyses: [], proportionalAnalyses: [] }
        ) ?? { sraAnalyses: [], proportionalAnalyses: [] }
      )
    })

    const someAnalysisCardData = computed(() => {
      return (
        (analyses.value.sraAnalyses && analyses.value.sraAnalyses.length > 0) ||
        (analyses.value.proportionalAnalyses &&
          analyses.value.proportionalAnalyses.length > 0)
      )
    })

    useChartRefresh({
      fetchChartFunction: getProjectOverviewData,
      routeName:
        ROUTE_NAME.PROJECT_OVERVIEW_DATA_VISUALIZATIONS as keyof RouteNamedMap,
    })

    const resetData = () => {
      data.value = undefined
    }

    return {
      data,
      analyses,

      someAnalysisCardData,

      resetData,

      getData,
      getProjectOverviewData,
    }
  }
)
